import { useEffect, useState } from "react";
import { CheckCircle, ClipboardList, AlertTriangle, Loader2, Pencil } from "lucide-react";
import toast from "react-hot-toast";
import { apiRequest } from "../ClockPortal";

interface PendingReview {
  id: string;
  periodStart: string;
  periodEnd: string;
  deadlineDate: string;
  isOverdue: boolean;
  /** "initial_response" = the original approve/request-edits ask; "confirm_edit" = a Team Lead
   *  edited one of this period's corrections and is waiting on the PCC to confirm or appeal it. */
  kind: "initial_response" | "confirm_edit";
}

interface ReviewDayEntry {
  workDate: string;
  clockInAt: string | null;
  clockOutAt: string | null;
  breakMinutes: number;
  hours: number | null;
}

interface ReviewAdjustment {
  id: string;
  workDate: string;
  field: string;
  requestedValue: string;
  reason: string;
  teamLeadDecision: "approved" | "edited" | "denied" | null;
  teamLeadOverrideValue: string | null;
  teamLeadNote: string | null;
  pccConfirmationStatus: "pending" | "confirmed" | "appealed" | null;
}

interface ReviewDetail {
  id: string;
  periodStart: string;
  periodEnd: string;
  deadlineDate: string;
  status: string;
  live: { days: ReviewDayEntry[]; totalHours: number };
  adjustments: ReviewAdjustment[];
}

const FIELD_LABELS: Record<string, string> = {
  clock_in: "Clock In", clock_out: "Clock Out", break_start: "Break Start", break_end: "Break End",
};

function formatDate(dateStr: string): string {
  // dateStr is YYYY-MM-DD — parse as local, not UTC, so it doesn't shift a day off.
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}

function formatTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}

function formatBreak(minutes: number): string {
  if (minutes <= 0) return "";
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts = [hrs > 0 ? `${hrs}h` : null, mins > 0 ? `${mins}m` : null].filter(Boolean);
  return ` · ${parts.join(" ")} break`;
}

export function TimesheetReviewTab({
  periodIdFromUrl,
  onRequestEdits,
  onPendingCountChange,
}: {
  periodIdFromUrl: string | null;
  onRequestEdits: (periodId: string, periodLabel: string) => void;
  /** Lets the tab bar's own badge dot stay in sync as reviews get resolved from inside this tab. */
  onPendingCountChange?: (count: number) => void;
}) {
  const [loading, setLoading] = useState(true);
  const [pending, setPendingState] = useState<PendingReview[]>([]);
  const setPending = (updater: PendingReview[] | ((prev: PendingReview[]) => PendingReview[])) => {
    setPendingState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      onPendingCountChange?.(next.length);
      return next;
    });
  };
  const [selectedId, setSelectedId] = useState<string | null>(periodIdFromUrl);
  const [detail, setDetail] = useState<ReviewDetail | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirmedId, setConfirmedId] = useState<string | null>(null);
  const [appealingAdjId, setAppealingAdjId] = useState<string | null>(null);
  const [appealReason, setAppealReason] = useState("");

  const reloadDetail = (id: string) =>
    apiRequest<{ data: ReviewDetail }>(`/api/v1/timesheet-review/${id}`)
      .then((res) => setDetail(res.data))
      .catch(() => setDetail(null));

  useEffect(() => {
    setLoading(true);
    apiRequest<{ data: PendingReview[] }>("/api/v1/timesheet-review/pending")
      .then((res) => {
        setPending(res.data);
        if (!periodIdFromUrl && res.data.length > 0) setSelectedId(res.data[0].id);
      })
      .catch(() => setPending([]))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    reloadDetail(selectedId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedId]);

  const approve = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      await apiRequest(`/api/v1/timesheet-review/${selectedId}/approve`, { method: "POST" });
      setConfirmedId(selectedId);
      setPending((prev) => prev.filter((p) => p.id !== selectedId));
      toast.success("Timesheet confirmed");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to confirm timesheet");
      // A 409 here means edits were already requested elsewhere (e.g. another tab, or a stale
      // link) since this screen loaded — re-fetch so the resolved-state view takes over instead
      // of leaving live buttons showing on an already-decided period.
      reloadDetail(selectedId);
    } finally {
      setSubmitting(false);
    }
  };

  const requestEdits = async () => {
    if (!selectedId || !detail) return;
    setSubmitting(true);
    try {
      await apiRequest(`/api/v1/timesheet-review/${selectedId}/request-edits`, { method: "POST" });
      setPending((prev) => prev.filter((p) => p.id !== selectedId));
      onRequestEdits(selectedId, `${formatDate(detail.periodStart)} – ${formatDate(detail.periodEnd)}`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to submit edit request");
      reloadDetail(selectedId);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmEdit = async (adjustmentId: string) => {
    if (!selectedId) return;
    setSubmitting(true);
    try {
      await apiRequest(`/api/v1/timesheet-review/${selectedId}/adjustments/${adjustmentId}/confirm-edit`, {
        method: "POST",
        body: JSON.stringify({ action: "confirm" }),
      });
      toast.success("Confirmed");
      setPending((prev) => prev.filter((p) => p.id !== selectedId));
      await reloadDetail(selectedId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to confirm");
    } finally {
      setSubmitting(false);
    }
  };

  const submitAppeal = async (adjustmentId: string) => {
    if (!selectedId) return;
    if (!appealReason.trim()) {
      toast.error("Please explain why you're appealing this correction");
      return;
    }
    setSubmitting(true);
    try {
      await apiRequest(`/api/v1/timesheet-review/${selectedId}/adjustments/${adjustmentId}/confirm-edit`, {
        method: "POST",
        body: JSON.stringify({ action: "appeal", appealReason: appealReason.trim() }),
      });
      toast.success("Appeal submitted — your Team Lead and Operations Manager have been notified");
      setAppealingAdjId(null);
      setAppealReason("");
      setPending((prev) => prev.filter((p) => p.id !== selectedId));
      await reloadDetail(selectedId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to submit appeal");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-400 py-10 justify-center">
        <Loader2 className="h-4 w-4 animate-spin" />
        <span className="text-sm">Loading timesheet review…</span>
      </div>
    );
  }

  if (confirmedId) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-800">Timesheet confirmed. Thank you!</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <ClipboardList className="h-10 w-10 text-gray-300 mx-auto mb-3" />
        <p className="text-sm text-gray-500">No timesheet review is pending right now.</p>
      </div>
    );
  }

  // A Team Lead's per-adjustment "edited" decision doesn't change the period's own status until
  // they finalize — so this can be true even while detail.status has already moved on to
  // pending_team_lead_review. Check for it before falling back to "already handled".
  const pendingConfirmation = detail.adjustments.find(
    (a) => a.teamLeadDecision === "edited" && a.pccConfirmationStatus === "pending"
  );

  if (pendingConfirmation) {
    const isAppealing = appealingAdjId === pendingConfirmation.id;
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium bg-blue-50 text-blue-800">
          <Pencil className="h-3.5 w-3.5 flex-shrink-0" />
          Your Team Lead adjusted one of your requested corrections — please confirm or appeal it below.
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-semibold text-gray-900">
              {formatDate(pendingConfirmation.workDate)} — {FIELD_LABELS[pendingConfirmation.field] ?? pendingConfirmation.field}
            </h3>
            <p className="text-xs text-gray-400 mt-1">Your reason: "{pendingConfirmation.reason}"</p>
          </div>

          <div className="px-4 py-3 space-y-2 text-sm border-b border-gray-100">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">You requested</span>
              <span className="font-medium text-gray-700">{formatTime(pendingConfirmation.requestedValue)}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-500">Team Lead's correction</span>
              <span className="font-semibold text-blue-700">{formatTime(pendingConfirmation.teamLeadOverrideValue)}</span>
            </div>
            {pendingConfirmation.teamLeadNote && (
              <p className="text-xs text-gray-500 italic pt-1">"{pendingConfirmation.teamLeadNote}"</p>
            )}
          </div>

          {isAppealing ? (
            <div className="px-4 py-3 space-y-2">
              <textarea
                value={appealReason}
                onChange={(e) => setAppealReason(e.target.value)}
                placeholder="Why do you disagree with this correction?"
                rows={3}
                className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-blue-400"
              />
              <div className="flex gap-2">
                <button
                  onClick={() => submitAppeal(pendingConfirmation.id)}
                  disabled={submitting}
                  className="flex-1 px-4 py-2 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Submit Appeal
                </button>
                <button
                  onClick={() => { setAppealingAdjId(null); setAppealReason(""); }}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="px-4 py-3 flex flex-col gap-2">
              <button
                onClick={() => confirmEdit(pendingConfirmation.id)}
                disabled={submitting}
                className="w-full px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Confirm This Is Correct
              </button>
              <button
                onClick={() => setAppealingAdjId(pendingConfirmation.id)}
                disabled={submitting}
                className="w-full px-4 py-2 text-sm font-medium rounded-lg border-2 border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                I Disagree — Appeal This
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // A stale email link (e.g. reopened after already responding) can still resolve to a real
  // period — show what happened instead of live action buttons, which would otherwise let
  // someone re-trigger approve/request-edits on an already-resolved period. The period is
  // promoted into the Team Lead/OM review chain immediately after either PCC action, so
  // "already handled" means anything other than the one status this screen's buttons act on —
  // not an enumerated list of every downstream status, which would need updating every time a
  // new one is added further down the chain.
  if (detail.status !== "pending_agent_review") {
    const label =
      detail.status === "edits_requested" ? "flagged for edits"
      : "confirmed";
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 text-center">
        <CheckCircle className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
        <p className="text-sm font-medium text-gray-800">
          {formatDate(detail.periodStart)} – {formatDate(detail.periodEnd)} has already been {label}.
        </p>
        <p className="text-xs text-gray-400 mt-1">No further action is needed here.</p>
      </div>
    );
  }

  const selectedPending = pending.find((p) => p.id === selectedId);

  return (
    <div className="space-y-3">
      {/* Top-of-tab indicator — visible as soon as the tab opens, not just the sidebar badge */}
      <div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium ${
          selectedPending?.isOverdue ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-800"
        }`}
      >
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
        {selectedPending?.isOverdue
          ? "This timesheet review is overdue — please respond as soon as possible."
          : "Timesheet review pending — please review and respond below."}
      </div>

      {pending.length > 1 && (
        <div className="flex gap-2 flex-wrap">
          {pending.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelectedId(p.id)}
              className={`text-xs px-2.5 py-1 rounded-full border ${
                p.id === selectedId ? "bg-blue-100 border-blue-300 text-blue-800" : "bg-white border-gray-200 text-gray-600"
              }`}
            >
              {p.kind === "confirm_edit" ? "Edit to review: " : ""}
              {formatDate(p.periodStart)}–{formatDate(p.periodEnd)}
              {p.isOverdue && <span className="ml-1 text-red-500">•</span>}
            </button>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100">
          <h3 className="text-sm font-semibold text-gray-900">
            {formatDate(detail.periodStart)} – {formatDate(detail.periodEnd)}
          </h3>
          <p className="text-xs text-amber-600 mt-1 flex items-start gap-1">
            <AlertTriangle className="h-3 w-3 mt-0.5 flex-shrink-0" />
            Please request edits by end of day on {formatDate(detail.deadlineDate)} if anything looks wrong.
          </p>
        </div>

        <div className="divide-y divide-gray-50">
          {detail.live.days.map((d) => (
            <div key={d.workDate} className="px-4 py-2.5 flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">{formatDate(d.workDate)}</p>
                <p className="text-xs text-gray-400">
                  {formatTime(d.clockInAt)} – {formatTime(d.clockOutAt)}{formatBreak(d.breakMinutes)}
                </p>
              </div>
              <span className="text-sm font-semibold text-gray-700">
                {d.hours != null ? `${d.hours.toFixed(2)}h` : "—"}
              </span>
            </div>
          ))}
        </div>

        <div className="px-4 py-2.5 bg-gray-50 flex items-center justify-between border-t border-gray-100">
          <span className="text-sm font-semibold text-gray-600">Total</span>
          <span className="text-sm font-bold text-gray-900">{detail.live.totalHours.toFixed(2)}h</span>
        </div>

        <div className="px-4 py-3 border-t border-gray-100 flex flex-col gap-2">
          <button
            onClick={approve}
            disabled={submitting}
            className="w-full px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
          >
            This Is Correct
          </button>
          <button
            onClick={requestEdits}
            disabled={submitting}
            className="w-full px-4 py-2 text-sm font-medium rounded-lg border-2 border-blue-600 text-blue-600 hover:bg-blue-50 disabled:opacity-50"
          >
            I Need to Request Edits
          </button>
        </div>
      </div>
    </div>
  );
}
